import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import TrashIcon from 'assets/images/icons/trashcan.svg'
import UnlinkIcon from '@assets/images/icons/v2/unlink.svg'
import { formatDate } from '_helpers/formatDate'
import { formatCurrency } from '_helpers/formatCurrency'
import { payKinds, assignmentTypes } from '@/modules/base/util/constants'
import { BookingService } from '@/modules/banking/services'
import { awaitableConfirmationModal } from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'
import lang from '@/lang'
import { Tooltip } from '@mui/material'

const { CASH } = payKinds
const { DISCOUNT, SURCHARGE, FEE, BANK_CHARGE } = assignmentTypes

const getTitle = (type) => {
  switch (type) {
    case DISCOUNT:
      return 'Skonto'
    case SURCHARGE:
      return 'Zuschlag'
    case FEE:
    case BANK_CHARGE:
      return 'Bankgebühr'
    default:
      return 'Zahlung'
  }
}

const useStyles = makeStyles((theme, props) => {
  return {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      borderTop: theme.border,
      borderBottom: theme.border,
      padding: '15px 0px',
      marginBottom: 20,
    },
    action: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      border: theme.border,
      borderRadius: '100%',
      background: '#fff',
      top: -13,
      right: -13,
      height: 30,
      width: 30,
      '&:hover': {
        cursor: 'pointer',
        '& > svg': {
          color: theme.colors.red,
          fill: theme.colors.red,
        },
      },
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    title: {
      fontWeight: '500',
    },
    amount: props => ({
      color: props.amount > 0 ? theme.green : theme.colors.red,
    }),
    subTitleItem: {
      fontSize: theme.fontSizes.smaller,
      color: theme.labelLight,
    },
  }
})

const PaymentStateItem = (props) => {
  const {
    bookingId,
    isNew,
    assignmentType,
    amount,
    bankAccountName,
    bookingDate,
    date,
    tempId,
    onRemove,
    onUnlink,
    type,
  } = props
  const classes = useStyles(props)

  const title = type === CASH ? 'Barzahlung' : getTitle(assignmentType)

  const onUnlinkBooking = async() => {
    const confirmed = await awaitableConfirmationModal({
      isOpen: true,
      title: lang.paymentState.unlink.confirm.title,
      description: lang.paymentState.unlink.confirm.description,
    })
    if (!confirmed) { return }

    await BookingService.removeAssignments(bookingId)
    onUnlink && onUnlink(bookingId)
  }

  const action = useMemo(() => {
    if (isNew && onRemove) {
      return (
        <Tooltip title={lang.paymentState.delete.tooltip}>
          <div className={classes.action} onClick={() => onRemove(tempId)}>
            <SVGIcon svg={TrashIcon} height='12px' width='12px' />
          </div>
        </Tooltip>
      )
    }

    if (bookingId) {
      return (
        <Tooltip title={lang.paymentState.unlink.title}>
          <div className={classes.action} onClick={onUnlinkBooking}>
            <SVGIcon svg={UnlinkIcon} height='12px' width='12px' />
          </div>
        </Tooltip>
      )
    }

    return null
  }, [bookingId, isNew, onRemove, tempId])

  return (
    <>
      <div className={classes.wrapper}>
        {action}
        <div className={classes.row}>
          <span className={classes.title}>{bankAccountName}</span>
          <span className={classes.amount}>{formatCurrency(amount)}</span>
        </div>
        <div className={classes.row}>
          <span className={classes.subTitleItem}>{title}</span>
          <span className={classes.subTitleItem}>{formatDate(bookingDate || date)}</span>
        </div>
      </div>
    </>

  )
}

export default PaymentStateItem
